import { useState } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useLinkPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useMutation, gql } from '@zenbusiness/application-commons-graphql';
import Text from '@zenbusiness/common-components/Text';
import Title from '@zenbusiness/common-components/Title';
import { useParams, Navigate, Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

import Breadcrumbs from '#client/components/Breadcrumbs';

import empty from '../../utils/empty';
import exists from '../../utils/exists';
import { getErrorsByField, throwExceptions } from '../../utils/validation';

const DOMAIN_DNS_MUTATE_CREATE = gql`
  mutation DomainDnsCreate($input: CreateDomainDnsInput!) {
    createDomainDns(input: $input) {
      domainDnsUuid
      type
      deleted
      updatedDatetime
      dnsConfiguration {
        ipAddress
        subdomain
        hostname
        priority
        text
        port
        weight
        destinationUrl
      }
    }
  }
`;

const DOMAIN_READ = gql`
  query DomainWithDNS($domainUuid: UUID!) {
    domain(domainUuid: $domainUuid) {
      domainUuid
      domainDns {
        domainDnsUuid
        type
        deleted
        updatedDatetime
        dnsConfiguration {
          ipAddress
          subdomain
          hostname
          text
          priority
          port
          weight
          destinationUrl
        }
      }
    }
  }
`;

// TODO Deduplicate the constants and html between Create and Mutate pages.

const messages = {
  type: {
    a: 'A',
    cname: 'CNAME',
    mx: 'MX',
    txt: 'TXT',
    srv: 'SRV',
    forward: 'FORWARD'
  },
  validation: {
    generic: 'Error',
    hostname: {
      format: 'Value must be in a valid domain name',
      required: 'Value is required',
      maxLength: 'Value may not be more than 254 characters',
      invalid_hostname: 'Value must be valid'
    },
    ipAddress: {
      format: 'IP Address must be formatted properly',
      required: 'IP Address is required',
      invalid_ip_address: 'IP Address must be formatted properly'
    },
    priority: {
      type: 'Priority must be a number or empty',
      maximum: 'Priority may not be more than 255',
      minimum: 'Priority must be at least 0'
    },
    text: {
      required: 'Text is required',
      minLength: 'Text is required',
      maxLength: 'Text may not be more than 254 characters'
    },
    subdomain: {
      type: 'Hostname must be text',
      maxLength: 'Hostname may not be more than 254 characters',
      max_type_a_record_per_subdomain: 'Only 1 A record allowed per hostname',
      max_type_cname_record_per_subdomain: 'Only 1 CNAME record allowed per hostname',
      max_type_srv_record_per_subdomain: 'Only 1 SRV record allowed per hostname',
      max_type_forward_record_per_subdomain: 'Only 1 FORWARD record allowed per hostname'
    },
    port: {
      type: 'Port must be a number or empty',
      maximum: 'Port may not be more than 65535',
      minimum: 'Port must be at least 0'
    },
    weight: {
      type: 'Weight must be a number or empty',
      maximum: 'Weight may not be more than 255',
      minimum: 'Weight must be at least 0'
    },
    destinationUrl: {
      format: 'Destination must be a valid URL',
      required: 'Destination URL is required',
      maxLength: 'URL may not be more than 254 characters',
      invalid_destination: 'Destination must be in a valid URL format'
    }
  },
  help: {
    common: `For instructions on setting up your website DNS check with your website
      or hosting provider, such as WIX, Squarespace, or Weebly.`,
    a: `An "A" record maps the domain name to its IP address. You can specify an IP
      Address for each A record that you add. An IP address is a 32-bit numeric address
      written as four numbers separated by periods. Each number can be zero to 255. For
      example: 123.123.123.123 could be an IP address.`,
    cname: `A "CNAME" record can only be created for a subdomain and points to another
      domain name. The other domain must be the domain that you want to access with both the
      hostname and domain name.
      For example: www.website.com`,
    mx: `"MX" record or mail exchange record.
      This record is responsible for accepting email messages on behalf of a domain name.
      Priority is the priority of the record; a lower value means more preferred.
      The preference can be any number from 0 to 255 and the default is usually 10.
      If you are entering or updating your MX record please enter the Priority and Value
      exactly as it’s listed in the instructions from your provider. `,
    txt: `Allows you to attach comments to a hostname, for example, SPF data to validate that email
      is being sent from the IP address of the domain's mail server. Enter the comments that you
      want to include in the Text field, to a maximum of 254 characters.
      For Example: This is some text.`,
    srv: `"SRV" (Service) records are custom DNS records.
      They are used to establish connections between a service and a hostname.
      SRV records are used to complete email connections for Microsoft Business email.`,
    forward: `Domain Forwarding or 301 redirect is used to redirect visitors from one URL to another.
      For example You own two domains, mydomain.com and mybiz.com.
      mydomain.com is connected to your existing website,
      but mybiz.com isn’t connected to a website and is just sitting there.
      You decide you want customers who type in mybiz.com to be taken to your existing website
      at mydomain.com so you set up a forward for mybiz.com.
      Forwards are good if you plan on having multiple domain names but one website for all of them.
      It also helps to increase site traffic!`
  },
  config: {
    hostname: 'Value',
    ipAddress: 'IP Address',
    subdomain: 'Hostname',
    text: 'Text',
    priority: 'Priority',
    port: 'Port',
    weight: 'Weight',
    destinationUrl: 'Destination URL'
  }
};

const recordPropsByType = {
  a: [{
    key: 'ipAddress',
    required: true,
    maxLength: 15
  }, {
    key: 'subdomain',
    required: false,
    maxLength: 254
  }],
  cname: [{
    key: 'hostname',
    required: true,
    maxLength: 254
  }, {
    key: 'subdomain',
    required: false,
    maxLength: 254
  }],
  mx: [{
    key: 'hostname',
    required: true,
    maxLength: 254
  }, {
    key: 'priority',
    required: false,
    maxLength: 3
  }, {
    key: 'subdomain',
    required: false,
    maxLength: 254
  }],
  txt: [{
    key: 'text',
    required: true,
    maxLength: 254
  }, {
    key: 'subdomain',
    required: false,
    maxLength: 254
  }],
  srv: [{
    key: 'hostname',
    required: true,
    maxLength: 254
  }, {
    key: 'subdomain',
    required: false,
    maxLength: 254
  }, {
    key: 'priority',
    required: false,
    maxLength: 3
  }, {
    key: 'port',
    required: true,
    maxLength: 5
  }, {
    key: 'weight',
    required: false,
    maxLength: 3
  }],
  forward: [{
    key: 'destinationUrl',
    required: true,
    maxLength: 254
  }, {
    key: 'subdomain',
    required: false,
    maxLength: 254
  }]
};

const StyledBoxRoot = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(2)
}));

const StyledBoxTitleSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(6)
}));

const StyledTitlePage = styled(Title)(({ theme }) => ({
  ...theme.typography.title_extraLarge
}));

const StyledTextFieldInput = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  width: '100%',
  borderRadius: theme.spacing(2),
  '& .MuiSelect-icon': {
    top: 'inherit'
  },
  '& .MuiInputBase-formControl': {
    borderRadius: theme.spacing(2)
  }
}));

const StyledSelectInput = styled(Select)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  width: '100%',
  borderRadius: theme.spacing(2),
  '& .MuiSelect-icon': {
    top: 'inherit'
  },
  '& .MuiInputBase-formControl': {
    borderRadius: theme.spacing(2)
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  width: '100%',
  borderRadius: theme.spacing(7),
  textTransform: 'capitalize',
  '&:disabled': {
    background: theme.palette.primary.focus30
  }
}));

const StyledForm = styled('form')(() => ({
  width: '100%'
}));

const StyledFormControl = styled(FormControl)(() => ({
  width: '100%'
}));

const StyledTitleHelp = styled(Title)(({ theme }) => ({
  marginBottom: theme.spacing(4)
}));

const StyledGridAction = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'row-reverse'
  }
}));

const defaultType = 'a';

const DomainDnsCreate = () => {
  const { businessEntityUuid } = useBusinessEntity();
  const { domainUuid } = useParams();
  const [createDns, {
    loading: createLoading,
    error: createError,
    data: createData
  }] = useMutation(DOMAIN_DNS_MUTATE_CREATE, {
    noThrow: true,
    update(cache, { data }) {
      try {
        const { createDomainDns } = data;
        const { domain } = cache.readQuery({
          query: DOMAIN_READ,
          variables: {
            domainUuid
          }
        })?.data || {};

        cache.writeQuery({
          query: DOMAIN_READ,
          data: {
            domain: {
              ...domain,
              domainDns: domain.domainDns.concat([createDomainDns])
            }
          }
        });
      } catch {
        // Failed to update cache. The queried data doesn't exit in the cache,
        // because the user hit the page directly or refreshed the page. Ignore error
      }
    }
  });

  throwExceptions(createError);

  const selectedDomainDnsUuid = createData && createData.createDomainDns
    && createData.createDomainDns.domainDnsUuid;
  const loading = createLoading;

  const dns = createData || {};

  const [changes, setChanges] = useState({});

  // Handle type
  const existingType = ((exists(dns) && exists(dns.type)) ? dns.type : defaultType);
  const changedType = (exists(changes.type) ? changes.type : null);
  const type = exists(changedType) ? changedType : existingType;

  const handleDomainsBreadcrumbClick = useLinkPressedCallback({
    elementName: 'domains_breadcrumb',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'dns_records'
  });

  const handleDomainBreadcrumbClick = useLinkPressedCallback({
    elementName: 'domain_breadcrumb',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'dns_records'
  });

  const handleOverviewBreadcrumbClick = useLinkPressedCallback({
    elementName: 'overview_breadcrumb',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'dns_records'
  });

  const handleRecordChange = (event) => {
    const {
      name,
      value
    } = event.target;

    setChanges({
      ...changes,
      [name]: value
    });
  };

  const unsaved = !empty(changes);

  const handleSaveClick = useLinkPressedCallback({
    elementName: 'save_dns',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'dns_records'
  });

  const handleDnsSave = () => {
    handleSaveClick();
    if (!loading && unsaved) {
      const { type: newType, ...newDnsRecord } = changes;
      const data = {
        type: newType || defaultType,
        dnsConfiguration: newDnsRecord
      };
      createDns({
        variables: {
          input: {
            domainUuid,
            data
          }
        }
      });
    }
  };

  if (!loading && selectedDomainDnsUuid) {
    // Navigate post creation
    return (<Navigate to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}#dns`} />);
  }

  // Process server validation errors
  const errorsByField = getErrorsByField(createError);

  // Handle dynamic record configuration fields
  const records = [];

  const recordTypes = [
    { value: 'a', label: 'A' },
    { value: 'cname', label: 'CNAME' },
    { value: 'mx', label: 'MX' },
    { value: 'txt', label: 'TXT' },
    { value: 'srv', label: 'SRV' },
    { value: 'forward', label: 'Forward' }
  ];

  if (type !== null) {
    recordPropsByType[type].forEach(({ key, required, maxLength }) => {
      const existingValue = ((exists(dns) && exists(dns.dnsConfiguration) && exists(dns.dnsConfiguration[key])) ? dns.dnsConfiguration[key] : '');
      const changedValue = (exists(changes[key]) ? changes[key] : null);
      // NOTE: Error object keys came in "/dnsConfiguration/<typeKey>" format,
      // therefore loop through all error keys and find the corresponding error tuple
      const errorTupleKey = Object.keys(errorsByField).find((k) => k.includes(key));
      const errorTuple = errorsByField[errorTupleKey];
      const errorKey = exists(errorTuple) && errorTuple[0];
      const errorMessage = exists(errorTuple) && errorTuple[1];
      let error = null;
      if (messages.validation[key] && errorKey) {
        error = messages.validation[key][errorKey] || errorMessage || messages.validation.generic;
      }

      records.push({
        key,
        value: exists(changedValue) ? changedValue : existingValue,
        required,
        error,
        maxLength
      });
    });
  }

  return (
    <StyledBoxRoot>
      <Breadcrumbs gutterBottom>
        <Button variant="text" color="primary" onClick={handleOverviewBreadcrumbClick} component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools`}>Overview</Button>
        <Button variant="text" color="primary" onClick={handleDomainsBreadcrumbClick} component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains`}>Domains</Button>
        <Button variant="text" color="primary" onClick={handleDomainBreadcrumbClick} component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}`}>Domain</Button>
        <Button variant="text" disabled color="primary" component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}/dns`}>DNS Record</Button>
      </Breadcrumbs>

      <StyledBoxTitleSection>
        <StyledTitlePage
          variant="md-alt"
          level={2}
        >
          Create a DNS Record
        </StyledTitlePage>
      </StyledBoxTitleSection>

      <div>
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={10}
        >
          <Grid container item xs={12} md={8}>
            <StyledForm noValidate autoComplete="off">
              <Grid container item xs={6} spacing={2}>
                <Grid item xs={12}>
                  <StyledFormControl variant="outlined">
                    <StyledSelectInput
                      id="type"
                      name="type"
                      value={type}
                      size="medium"
                      onChange={handleRecordChange}
                    >
                      {recordTypes.map(({
                        value, label
                      }) => (
                        <MenuItem
                          key={value}
                          value={value}
                        >
                          {label}
                        </MenuItem>
                      ))}
                    </StyledSelectInput>
                  </StyledFormControl>
                </Grid>
              </Grid>
              <StyledFormControl component="fieldset">
                <Grid container item xs={12} spacing={2}>
                  {records.map(({
                    key, value, required, error, maxLength
                  }) => (
                    <Grid key={key} item xs={6}>
                      <StyledTextFieldInput
                        id={key}
                        variant="outlined"
                        size="medium"
                        label={messages.config[key]}
                        name={key}
                        value={value}
                        onChange={handleRecordChange}
                        helperText={error}
                        error={!!error}
                        required={required}
                        inputProps={{
                          maxLength
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </StyledFormControl>

              <StyledGridAction container item xs={12} spacing={2}>
                <Grid item xs={12} md={6}>
                  <StyledButton variant="contained" color="primary" onClick={handleDnsSave} disabled={loading || !unsaved}>save</StyledButton>
                </Grid>
                <Grid item xs={12} md={6}>
                  <StyledButton variant="outlined" color="primary" component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}`}>cancel</StyledButton>
                </Grid>
              </StyledGridAction>
            </StyledForm>
          </Grid>
          {type && (
            <Grid item xs={12} md={8}>
              <StyledTitleHelp variant="sm-alt">
                {`${messages.type[type]} Record Help`}
              </StyledTitleHelp>
              <Text size="md" gutterBottom>
                {messages.help.common}
              </Text>
              <Text size="md">
                {messages.help[type]}
              </Text>
            </Grid>
          )}
        </Grid>
      </div>
    </StyledBoxRoot>
  );
};

export default DomainDnsCreate;
